import { supabase } from "./client";
import { getCurrentEmpresaId } from "./auth-utils";

export interface ProvaAgendada {
  id: string;
  data_prova: string;
  hora_prova: string;
  local_prova: "atelier" | "cliente" | "video";
  observacoes?: string;
  confirmada: boolean;
  data_agendamento: string;
}

export interface HistoricoProvaCliente {
  cliente_id: string;
  cliente_nome: string;
  provas: ProvaAgendada[];
}

/**
 * Atualizar histórico de provas agendadas para um cliente
 */
export async function adicionarProvaAoCliente(
  clienteId: string,
  clienteNome: string,
  pedidoNumero: string,
  prova: ProvaAgendada
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não identificada" };
    }

    // Obter cliente
    const { data: clienteData, error: clienteError } = await supabase
      .from("customers")
      .select("provas_agendadas")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (clienteError) {
      console.error("Erro ao buscar cliente:", clienteError);
      return { ok: false, error: "Cliente não encontrado" };
    }

    // Preparar histórico de provas
    const provasAtuais = (clienteData?.provas_agendadas || []) as any[];
    const novaProva = {
      ...prova,
      pedido_numero: pedidoNumero,
      cliente_nome: clienteNome,
    };

    // Adicionar nova prova ao histórico
    const novasProvas = [...provasAtuais, novaProva];

    console.log("🎬 [PROVAS] Salvando prova:", { clienteId, clienteNome, pedidoNumero, provasTotal: novasProvas.length });

    // Atualizar cliente com novo histórico
    const { error: updateError } = await supabase
      .from("customers")
      .update({ provas_agendadas: novasProvas })
      .eq("id", clienteId)
      .eq("empresa_id", empresaId);

    if (updateError) {
      console.error("Erro ao atualizar histórico de provas:", updateError);
      return { ok: false, error: "Erro ao salvar histórico" };
    }

    console.log("✅ [PROVAS] Prova salva com sucesso!");
    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao adicionar prova ao cliente:", error);
    return { ok: false, error: error?.message ?? "Erro ao adicionar prova" };
  }
}

/**
 * Obter histórico de provas de um cliente
 */
export async function obterProvasDoCliente(
  clienteId: string
): Promise<ProvaAgendada[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data, error } = await supabase
      .from("customers")
      .select("provas_agendadas")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (error) {
      console.error("Erro ao buscar provas do cliente:", error);
      return [];
    }

    console.log("🎬 [PROVAS] Cliente ID:", clienteId, "| Provas encontradas:", data?.provas_agendadas || []);
    return (data?.provas_agendadas || []) as ProvaAgendada[];
  } catch (error) {
    console.error("Erro ao obter provas do cliente:", error);
    return [];
  }
}

/**
 * Cancelar agendamento de prova de um cliente
 */
export async function cancelarProvaDoCliente(
  clienteId: string,
  provaId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não identificada" };
    }

    const { data: clienteData, error: clienteError } = await supabase
      .from("customers")
      .select("provas_agendadas")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (clienteError) {
      return { ok: false, error: "Cliente não encontrado" };
    }

    const provasAtuais = (clienteData?.provas_agendadas || []) as any[];
    const provasAtualizadas = provasAtuais.filter((p) => p.id !== provaId);

    const { error: updateError } = await supabase
      .from("customers")
      .update({ provas_agendadas: provasAtualizadas })
      .eq("id", clienteId)
      .eq("empresa_id", empresaId);

    if (updateError) {
      return { ok: false, error: "Erro ao atualizar histórico" };
    }

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao cancelar prova do cliente:", error);
    return { ok: false, error: error?.message ?? "Erro ao cancelar prova" };
  }
}
